import { createTheme } from '@mui/material/styles'
import type { ThemeOptions } from '@mui/material/styles'
import { fontFamilies } from '@/lib/fonts'

// Black Book brand colors - Official color palette
const brandColors = {
  primary: {
    main: '#2D2926', // BLACK C - Primary brand color
    light: '#4F4B48', // BLACK C 50%
    dark: '#161413', // BLACK C 50% shade
  },
  secondary: {
    main: '#3CDBC0', // 333 C - Secondary teal color
    light: '#9AE9D9', // 333 C 50%
    dark: '#00C9A7', // 333 C 75%
  },
  tertiary: {
    main: '#FE5000', // ORANGE 021 C - Accent color
    light: '#FF6B33', // ORANGE 021 C 50%
    dark: '#F34A00', // ORANGE 021 C 75%
  },
}

// Light theme colors
const lightColors = {
  background: {
    default: '#F5F5F5',
    paper: '#FFFFFF',
    cardPanel: '#FAFAFA',
  },
  text: {
    primary: '#2D2926', // BLACK C
    secondary: '#75726F', // BLACK C 50%
    disabled: '#CCCCCC',
  },
  divider: '#E0E0E0',
  cardPanel: {
    header: '#2D2926', // BLACK C for card panel headers
    border: '#CCCCCC',
    hover: '#F0F0F0',
  }
}

// Dark theme colors
const darkColors = {
  background: {
    default: '#121212', // MUI dark theme default
    paper: '#1E1E1E', // Slightly lighter than default
    cardPanel: '#2A2A2A', // Card panel background
  },
  text: {
    primary: '#FFFFFF', // White text on dark background
    secondary: '#B0B0B0', // Light gray secondary text
    disabled: '#666666', // Dark gray disabled text
  },
  divider: '#424242', // Dark divider
  cardPanel: {
    header: '#3CDBC0', // Use teal for card panel headers in dark mode
    border: '#424242', // Dark border
    hover: '#333333', // Dark hover state
  }
}

// Base theme options shared between light and dark themes
const getBaseThemeOptions = (mode: 'light' | 'dark'): ThemeOptions => {
  const colors = mode === 'light' ? lightColors : darkColors
  
  return {
    palette: {
      mode,
      primary: {
        ...brandColors.primary,
        contrastText: '#FFFFFF',
      },
      secondary: {
        ...brandColors.secondary,
        contrastText: mode === 'light' ? '#FFFFFF' : '#000000',
      },
      tertiary: {
        ...brandColors.tertiary,
        contrastText: '#FFFFFF',
      },
      background: colors.background,
      text: colors.text,
      divider: colors.divider,
      // Custom colors for card panel components
      cardPanel: colors.cardPanel,
    },
    typography: {
      // Brand typography: Primary font is Roboto for body text
      fontFamily: fontFamilies.body,
      fontSize: 14, // Base font size
      h1: {
        fontFamily: fontFamilies.heading,
        fontSize: '2.0em',
        fontWeight: 'bold',
        color: colors.text.primary,
      },
      h2: {
        fontFamily: fontFamilies.heading,
        fontSize: '1.8em',
        fontWeight: 'bold',
        color: colors.text.primary,
      },
      h3: {
        fontFamily: fontFamilies.heading,
        fontSize: '1.5em',
        fontWeight: 'bold',
        color: colors.text.primary,
      },
      h4: {
        fontFamily: fontFamilies.heading,
        fontSize: '1.3em',
        fontWeight: 'bold',
        color: colors.text.primary,
      },
      h5: {
        fontFamily: fontFamilies.heading,
        fontSize: '1.2em',
        fontWeight: 'bold',
        color: colors.text.primary,
      },
      h6: {
        fontFamily: fontFamilies.heading,
        fontSize: '1.1em',
        fontWeight: 'bold',
        color: colors.text.primary,
      },
      body1: {
        fontSize: '1.0em',
        color: colors.text.primary,
      },
      body2: {
        fontSize: '0.9em',
        color: colors.text.secondary,
      },
      button: {
        fontSize: '1.0em',
        textTransform: 'none', // Don't auto-uppercase buttons
      },
    },
    components: {
      // Customize MUI components to match legacy design
      MuiCssBaseline: {
        styleOverrides: {
          '*': {
            boxSizing: 'border-box',
          },
          html: {
            margin: 0,
            padding: 0,
            width: '100%',
            height: '100%',
          },
          body: {
            margin: 0,
            padding: 0,
            fontSize: '14px',
            fontFamily: fontFamilies.body,
            backgroundColor: colors.background.default,
            width: '100%',
            height: '100%',
          },
          '#__next': {
            margin: 0,
            padding: 0,
            width: '100%',
            height: '100%',
          },
          // Europa Bold font declaration - will work when font files are added
          '@font-face': {
            fontFamily: 'Europa Bold',
            fontStyle: 'normal',
            fontWeight: 700,
            fontDisplay: 'swap',
            src: `
              local('Europa Bold'),
              url('/fonts/europa-bold.woff2') format('woff2'),
              url('/fonts/europa-bold.woff') format('woff')
            `,
          }
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: '4px', // Legacy rounded corners
            border: `1px solid ${colors.cardPanel.border}`,
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            paddingTop: '24px !important', // More vertical breathing room
            paddingBottom: '24px !important', // More vertical breathing room
            '&:last-child': {
              paddingBottom: '24px !important', // Override MUI's last-child padding reset
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: '14px',
            fontFamily: fontFamilies.body,
            padding: '8px 12px', // Compact table cells
          },
          head: {
            backgroundColor: colors.cardPanel.header,
            color: mode === 'light' ? '#FFFFFF' : '#000000',
            fontWeight: 'bold',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: '42px',
          },
          indicator: {
            backgroundColor: brandColors.secondary.main,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontSize: '14px',
            fontFamily: fontFamilies.body,
            textTransform: 'none',
            minHeight: '42px',
            color: colors.text.secondary,
            '&.Mui-selected': {
              color: brandColors.secondary.main,
              fontWeight: 'bold',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: mode === 'light' 
                ? 'rgba(60, 219, 192, 0.1)' 
                : 'rgba(60, 219, 192, 0.2)', // Slightly more visible in dark mode
            },
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            paddingTop: 0,
            paddingBottom: 0,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            '& .MuiTypography-root': {
              color: mode === 'dark' ? '#FFFFFF !important' : '#2D2926 !important',
            },
            '& .MuiToolbar-root': {
              color: mode === 'dark' ? '#FFFFFF' : '#2D2926',
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            border: `2px solid ${brandColors.secondary.main}`,
            color: brandColors.secondary.main,
            backgroundColor: 'transparent',
            fontWeight: 700,
            fontSize: '0.8rem',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: brandColors.secondary.main,
              color: mode === 'light' ? '#FFFFFF' : '#000000',
            },
            '&.Mui-selected': {
              backgroundColor: brandColors.secondary.main,
              color: mode === 'light' ? '#FFFFFF' : '#000000',
              '&:hover': {
                backgroundColor: brandColors.secondary.dark,
                color: mode === 'light' ? '#FFFFFF' : '#000000',
              },
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : colors.divider, // Standard MUI light, visible dark
              },
              '&:hover fieldset': {
                borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : brandColors.secondary.light, // Black on light, teal on dark
              },
              '&.Mui-focused fieldset': {
                borderColor: mode === 'light' ? brandColors.primary.main : brandColors.secondary.main, // Black focus on light, teal on dark
                borderWidth: 2,
              },
              '&.Mui-error fieldset': {
                borderColor: brandColors.tertiary.main,
              },
            },
            '& .MuiInputLabel-root': {
              color: colors.text.secondary,
              '&.Mui-focused': {
                color: mode === 'light' ? brandColors.primary.main : brandColors.secondary.main,
              },
              '&.Mui-error': {
                color: brandColors.tertiary.main,
              },
            },
            '& .MuiInputBase-input': {
              color: colors.text.primary,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : colors.divider,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : brandColors.secondary.light,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'light' ? brandColors.primary.main : brandColors.secondary.main,
              borderWidth: 2,
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: brandColors.tertiary.main,
            },
          },
          input: {
            color: colors.text.primary,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: colors.text.secondary,
            '&.Mui-focused': {
              color: mode === 'light' ? brandColors.primary.main : brandColors.secondary.main,
            },
            '&.Mui-error': {
              color: brandColors.tertiary.main,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : colors.divider,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : brandColors.secondary.light,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'light' ? brandColors.primary.main : brandColors.secondary.main,
              borderWidth: 2,
            },
          },
          select: {
            color: colors.text.primary,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : colors.divider,
              },
              '&:hover fieldset': {
                borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : brandColors.secondary.light,
              },
              '&.Mui-focused fieldset': {
                borderColor: mode === 'light' ? brandColors.primary.main : brandColors.secondary.main,
                borderWidth: 2,
              },
            },
          },
          inputRoot: {
            color: colors.text.primary,
          },
          paper: {
            backgroundColor: colors.background.paper,
            border: `1px solid ${colors.divider}`,
          },
          option: {
            color: colors.text.primary,
            '&:hover': {
              backgroundColor: mode === 'light' 
                ? 'rgba(60, 219, 192, 0.1)' 
                : 'rgba(60, 219, 192, 0.2)',
            },
            '&.Mui-focused': {
              backgroundColor: mode === 'light' 
                ? 'rgba(60, 219, 192, 0.1)' 
                : 'rgba(60, 219, 192, 0.2)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '0', // Remove rounded corners
            padding: '8px 16px',
            transition: 'all 0.2s ease-in-out',
            fontWeight: 700, // Make button text bold
          }
        },
        variants: [
          // Primary buttons (default)
          {
            props: { color: 'primary' },
            style: {
              backgroundColor: 'transparent',
              color: brandColors.primary.main,
              border: `2px solid ${brandColors.primary.main}`,
              '&:hover': {
                backgroundColor: brandColors.primary.main,
                color: '#FFFFFF',
              },
              '&:active': {
                backgroundColor: brandColors.primary.dark,
                borderColor: brandColors.primary.dark,
              },
            },
          },
          // Secondary/positive buttons
          {
            props: { color: 'secondary' },
            style: {
              backgroundColor: 'transparent',
              color: brandColors.secondary.main,
              border: `2px solid ${brandColors.secondary.main}`,
              '&:hover': {
                backgroundColor: brandColors.secondary.main,
                color: '#FFFFFF',
              },
              '&:active': {
                backgroundColor: brandColors.secondary.dark,
                borderColor: brandColors.secondary.dark,
              },
            },
          },
          // Filled primary button 
          {
            props: { variant: 'contained', color: 'primary' },
            style: {
              backgroundColor: brandColors.primary.main,
              color: '#FFFFFF',
              border: `2px solid ${brandColors.primary.main}`,
              '&:hover': {
                backgroundColor: brandColors.primary.light,
                borderColor: brandColors.primary.light,
              },
              '&:active': {
                backgroundColor: brandColors.primary.dark,
                borderColor: brandColors.primary.dark,
              },
            },
          },
          // Filled secondary/positive button
          {
            props: { variant: 'contained', color: 'secondary' },
            style: {
              backgroundColor: brandColors.secondary.main,
              color: mode === 'light' ? '#FFFFFF' : '#000000',
              border: `2px solid ${brandColors.secondary.main}`,
              '&:hover': {
                backgroundColor: brandColors.secondary.light,
                borderColor: brandColors.secondary.light,
                color: mode === 'light' ? '#FFFFFF' : '#000000',
              },
              '&:active': {
                backgroundColor: brandColors.secondary.dark,
                borderColor: brandColors.secondary.dark,
                color: mode === 'light' ? '#FFFFFF' : '#000000',
              },
            },
          },
        ],
      },
    },
    // Custom breakpoints for legacy fixed-width design
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960, // Legacy container width
        lg: 1005, // Legacy max container width
        xl: 1200,
      },
    },
  }
}

// Create light and dark themes
export const lightTheme = createTheme(getBaseThemeOptions('light'))
export const darkTheme = createTheme(getBaseThemeOptions('dark'))

// Default export for backward compatibility
export const theme = lightTheme